import type { CardFunding, CreditTransaction } from "./credits";
import type { TransactionContext } from "../../transaction-context";

export const AUTOMATIC_BILLING_OFFER_PROGRAMS = {
  FAST_WEB_WELCOME_V1: "fast_web_welcome_v1",
} as const;

export type AutomaticBillingOfferProgram =
  (typeof AUTOMATIC_BILLING_OFFER_PROGRAMS)[keyof typeof AUTOMATIC_BILLING_OFFER_PROGRAMS];

export const AUTOMATIC_BILLING_OFFER_STATUSES = {
  ELIGIBLE: "eligible",
  RESERVED: "reserved",
  GRANTED: "granted",
  FORFEITED: "forfeited",
  DENIED: "denied",
  CANCELED: "canceled",
  REVERSED: "reversed",
} as const;

export type AutomaticBillingOfferStatus =
  (typeof AUTOMATIC_BILLING_OFFER_STATUSES)[keyof typeof AUTOMATIC_BILLING_OFFER_STATUSES];

export const AUTOMATIC_BILLING_OFFER_FUNDING_KINDS = {
  PAYG: "payg",
  SUBSCRIPTION: "subscription",
} as const;

export type AutomaticBillingOfferFundingKind =
  (typeof AUTOMATIC_BILLING_OFFER_FUNDING_KINDS)[keyof typeof AUTOMATIC_BILLING_OFFER_FUNDING_KINDS];

export const FAST_WEB_WELCOME_OFFER = {
  program: AUTOMATIC_BILLING_OFFER_PROGRAMS.FAST_WEB_WELCOME_V1,
  amountCents: 500,
} as const;

export const FAST_WEB_WELCOME_CREDIT_REASON = "fast_web_welcome_credit";

export const FAST_WEB_ONBOARDING_SOURCE = "fast_web";
export const FAST_WEB_AUTO_TOP_UP_CONSENT_VERSION = "fast_web_auto_top_up_v1";

export type FastWebAutoTopUpConsentVersion =
  typeof FAST_WEB_AUTO_TOP_UP_CONSENT_VERSION;

export type AutomaticBillingOfferDeniedReason =
  | "legacy_acquisition_reward_already_granted"
  | "missing_fingerprint"
  | "non_card_payment_method"
  | "ineligible_card_funding"
  | "fingerprint_linked_to_banned_account"
  | "fingerprint_shared_across_accounts"
  | "fingerprint_already_claimed"
  | "manual_promotion_code_applied";

export interface AutomaticBillingOfferDecision {
  id: string;
  program: AutomaticBillingOfferProgram;
  teamId: string;
  ownerUserId: string;
  fundingKind: AutomaticBillingOfferFundingKind;
  status: AutomaticBillingOfferStatus;
  phoneVerifiedAtCheckout: boolean;
  deniedReason: AutomaticBillingOfferDeniedReason | null;
  stripeCheckoutSessionId: string | null;
  stripePaymentIntentId: string | null;
  stripeInvoiceId: string | null;
  cardFingerprint: string | null;
  creditTransactionId: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface GrantAutomaticBillingOfferResult {
  decision: AutomaticBillingOfferDecision;
  transaction: CreditTransaction | null;
}

export interface InsertAutomaticBillingOfferDecisionInput {
  id: string;
  program: AutomaticBillingOfferProgram;
  teamId: string;
  ownerUserId: string;
  fundingKind: AutomaticBillingOfferFundingKind;
  status: AutomaticBillingOfferStatus;
  phoneVerifiedAtCheckout: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface UpdateAutomaticBillingOfferDecisionInput {
  id: string;
  fundingKind?: AutomaticBillingOfferFundingKind;
  status?: AutomaticBillingOfferStatus;
  phoneVerifiedAtCheckout?: boolean;
  deniedReason?: AutomaticBillingOfferDeniedReason | null;
  stripeCheckoutSessionId?: string | null;
  stripePaymentIntentId?: string | null;
  stripeInvoiceId?: string | null;
  cardFingerprint?: string | null;
  creditTransactionId?: string | null;
  updatedAt: Date;
}

export interface ReserveAutomaticBillingOfferInput {
  teamId: string;
  ownerUserId: string;
  fundingKind: AutomaticBillingOfferFundingKind;
  phoneVerifiedAtCheckout: boolean;
  ctx?: TransactionContext;
}

export interface RecordAutomaticBillingOfferCheckoutInput {
  decisionId: string;
  stripeCheckoutSessionId: string;
  ctx?: TransactionContext;
}

export interface SettleAutomaticBillingOfferInput {
  program: AutomaticBillingOfferProgram;
  teamId: string;
  ownerUserId: string;
  decisionId?: string;
  stripeCheckoutSessionId?: string;
  stripePaymentIntentId?: string;
  stripeInvoiceId?: string;
  cardFingerprint: string | null;
  cardFunding: CardFunding | null;
  manualPromotionCodeApplied: boolean;
  ctx?: TransactionContext;
}

export interface ReverseAutomaticBillingOfferInput {
  stripeCheckoutSessionId?: string;
  stripeInvoiceId?: string;
  stripePaymentIntentId?: string;
  stripeRefundId?: string;
  stripeDisputeId?: string;
  ctx?: TransactionContext;
}
